import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";

import { useLocalization } from "@fluent/react";
import { useQueryClient } from "@tanstack/react-query";
import { useNotificationContext } from "cerulean";
import { useSearchParams } from "react-router-dom";

import { useAccountsContext } from "../../../../../components/contexts/AccountContext";
import { useCurrentUser } from "../../../../../components/contexts/CurrentUserContext";
import {
  LIVE_LEDGER_ACCOUNT_QUERY_KEY,
  LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY,
  LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY,
  LIVE_LEDGER_BAI_CODES_QUERY_KEY,
  LIVE_LEDGER_CATEGORY_QUERY_KEY,
  LIVE_LEDGER_DATE_FROM_QUERY_KEY,
  LIVE_LEDGER_DATE_TO_QUERY_KEY,
  LIVE_LEDGER_METHOD_QUERY_KEY,
  LIVE_LEDGER_SEARCH_QUERY_KEY,
  LIVE_LEDGER_TYPE_QUERY_KEY,
  TRANSACTION_DETAIL_QUERY_KEY,
  TRANSACTION_ID_PARAM,
} from "../../constants";
import { FilterProvider, useFilterContext } from "../../context/FilterContext";
import {
  buildSummaryCaption,
  hasFutureDate,
  parseFilterDate,
  TYPE_VALID_VALUES,
} from "../../helpers";
import { useCanWriteTransactions } from "../../hooks/useCanWriteTransactions";
import { useIsMultiEntity } from "../../hooks/useIsMultiEntity";
import { useLiveLedgerCategories } from "../../hooks/useLiveLedgerCategories";
import { useTransactionEnrichmentEnabled } from "../../hooks/useTransactionEnrichmentEnabled";
import { useUserFeatures } from "../../../../../components/contexts/UserFeaturesContext";
import { pickErrorMapping } from "../../../receipts/components/UploadReceiptDialog/errorMapping";
import useReceiptUploadConfig from "../../../receipts/components/UploadReceiptDialog/useReceiptUploadConfig";
import { useCanWriteReceipts } from "../../../receipts/hooks/useCanWriteReceipts";
import { useTransactionDetail } from "../../hooks/useTransactionDetail";
import { useTransactionPanelSelection } from "../../hooks/useTransactionPanelSelection";
import useTransactionReceiptUpload from "../../hooks/useTransactionReceiptUpload";
import { useTransactionExport } from "../../hooks/useTransactionExport";
import { useTransactionsList } from "../../hooks/useTransactionsList";
import { usePageParam } from "../../hooks/usePageParam";
import { useLiveLedgerSettings } from "../../storage";
import { useTransactionSummary } from "../../hooks/useTransactionSummary";
import NewThreadDialog from "../../../../../components/NewThreadDialog";
import { buildLedgerColumns, LedgerMobileRow } from "../ledgerColumns";
import LedgerPanelFooter from "../LedgerPanelFooter/LedgerPanelFooter";
import { buildLedgerPanelSections } from "../ledgerPanelSections";
import LedgerPanelHeader from "../LedgerPanelHeader";
import ReceiptPreviewOverlay from "../ReceiptPreviewOverlay";
import TransactionDetailsPanel from "../TransactionDetailsPanel";
import ExportDialog from "../ExportDialog/ExportDialog";
import TransactionSummary from "../TransactionSummary";
import TransactionTable from "../TransactionTable";

import TransactionLedgerFilters from "./TransactionLedgerFilters";
import { queryKeysForFilters } from "./filterRegistry";

import type { TransactionLedgerFilterId } from "./filterRegistry";
import type { FilterStorage } from "../../context/FilterContext";
import type { FilterBarSearch } from "../FilterBar";
import type { TransactionPanelSection } from "../TransactionDetailsPanel";
import type { TransactionTableColumn } from "../TransactionTable";

const RECEIPT_THUMBNAIL_POLL_MS = 2000;
const RECEIPT_THUMBNAIL_POLL_ATTEMPTS = 5;

const accountNumberFor = (
  transaction: API.Transaction,
  accounts: { id: string; number: string }[]
) => accounts.find((account) => account.id === transaction.account_id)?.number;

export type TransactionLedgerColumnId =
  | "date"
  | "description"
  | "descriptionWithDate"
  | "account"
  | "category"
  | "receipt"
  | "amount"
  | "balance";

export type TransactionLedgerPanelSectionId =
  | "details"
  | "receipt"
  | "check"
  | "bai2"
  | "tags"
  | "notes";

export type TransactionLedgerPagination = {
  enabled: boolean;
  pageSize: number;
  pageSizeOptions?: number[];
};

export type TransactionLedgerSearch = FilterBarSearch;

export type TransactionLedgerStateStorage = FilterStorage;

export type TransactionLedgerProps = {
  columns: TransactionLedgerColumnId[];
  pagination: TransactionLedgerPagination;
  receipts?: boolean;
  panel?: TransactionLedgerPanelSectionId[];
  filters?: TransactionLedgerFilterId[];
  filtersAlwaysVisible?: boolean;
  /**
   * Scopes the query to these accounts without going through the
   * "accounts" filter's URL/local state, so no accounts chip renders.
   * Do not also pass "accounts" in `filters` when using this.
   */
  scopedAccountIds?: string[];
  search?: TransactionLedgerSearch;
  stateStorage?: TransactionLedgerStateStorage;
  onFilterStateChange?: (filterState: Record<string, string>) => void;
  summary?: boolean;
  emptyState: ReactNode;
  replyTime: string;
  headerFill?: boolean;
};

const BALANCE_ALLOWED_KEYS = new Set([
  LIVE_LEDGER_ACCOUNT_QUERY_KEY,
  LIVE_LEDGER_DATE_FROM_QUERY_KEY,
  LIVE_LEDGER_DATE_TO_QUERY_KEY,
]);

const shouldShowBalanceColumn = (
  selectedAccountIds: string[],
  filterState: Record<string, string>
): boolean =>
  selectedAccountIds.length === 1 &&
  Object.entries(filterState).every(
    ([key, value]) => !value || BALANCE_ALLOWED_KEYS.has(key)
  );

const TransactionLedgerContent = ({
  columns: columnIds,
  pagination,
  receipts = false,
  panel,
  filters: filterIds = [],
  filtersAlwaysVisible = false,
  scopedAccountIds,
  search = false,
  stateStorage,
  onFilterStateChange,
  summary: showSummary = true,
  emptyState,
  replyTime,
  headerFill,
}: TransactionLedgerProps) => {
  const { l10n } = useLocalization();
  const queryClient = useQueryClient();
  const { sendNotification } = useNotificationContext();
  const { accounts } = useAccountsContext();
  const { currentUser } = useCurrentUser();
  const isMultiEntity = useIsMultiEntity(accounts ?? []);
  const { receipts_enabled: receiptsEnabled = false } = useUserFeatures();
  const receiptsActive = receipts && receiptsEnabled;
  const canWriteReceipts = useCanWriteReceipts();
  const { uploadingTransactionIds, upload, remove } =
    useTransactionReceiptUpload();
  const [previewReceipt, setPreviewReceipt] =
    useState<API.TransactionReceipt | null>(null);
  const [awaitingThumbnail, setAwaitingThumbnail] = useState<
    Record<string, number>
  >({});
  const userId = currentUser?.id?.toString();
  const [ledgerSettings, setLedgerSettings] = useLiveLedgerSettings(userId);
  const { filterState } = useFilterContext();
  useEffect(() => {
    onFilterStateChange?.(filterState);
  }, [filterState, onFilterStateChange]);
  const {
    summary,
    isFiltered: isSummaryFiltered,
    isLoading: isSummaryLoading,
    error: summaryError,
  } = useTransactionSummary(
    showSummary,
    ledgerSettings.includeInternalTransfers
  );
  const { page, setPage } = usePageParam();
  const [pageSize, setPageSize] = useState(pagination.pageSize);
  const [reportedTransaction, setReportedTransaction] =
    useState<API.Transaction | null>(null);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);

  const resetPage = useCallback(() => setPage(1), [setPage]);
  const handlePageSizeChange = useCallback(
    (nextPageSize: number) => {
      setPageSize(nextPageSize);
      setPage(1);
    },
    [setPage]
  );

  const prevPageSizeRef = useRef(pagination.pageSize);
  useEffect(() => {
    if (prevPageSizeRef.current === pagination.pageSize) return;
    prevPageSizeRef.current = pagination.pageSize;
    setPageSize(pagination.pageSize);
    setPage(1);
  }, [pagination.pageSize, setPage]);

  const { config: receiptConfig } = useReceiptUploadConfig(receiptsActive);

  const parseFilterNumber = (raw: string | undefined): number | undefined => {
    if (!raw) return undefined;
    const n = Number(raw);
    return Number.isNaN(n) ? undefined : n;
  };
  const splitFilter = (raw: string | undefined): string[] =>
    raw ? raw.split(",").filter(Boolean) : [];

  const filterAccountIds = splitFilter(
    filterState[LIVE_LEDGER_ACCOUNT_QUERY_KEY]
  );
  const selectedAccountIds = scopedAccountIds ?? filterAccountIds;
  const isScopedToNoAccounts =
    scopedAccountIds !== undefined && scopedAccountIds.length === 0;
  const selectedCategories = splitFilter(
    filterState[LIVE_LEDGER_CATEGORY_QUERY_KEY]
  );
  const selectedBaiCodes = splitFilter(
    filterState[LIVE_LEDGER_BAI_CODES_QUERY_KEY]
  );
  const selectedMethods = splitFilter(
    filterState[LIVE_LEDGER_METHOD_QUERY_KEY]
  );
  const amountMin = parseFilterNumber(
    filterState[LIVE_LEDGER_AMOUNT_MIN_QUERY_KEY]
  );
  const amountMax = parseFilterNumber(
    filterState[LIVE_LEDGER_AMOUNT_MAX_QUERY_KEY]
  );
  const settledFrom = parseFilterDate(
    filterState[LIVE_LEDGER_DATE_FROM_QUERY_KEY]
  );
  const settledTo = parseFilterDate(filterState[LIVE_LEDGER_DATE_TO_QUERY_KEY]);
  const hasFutureDates = hasFutureDate(settledFrom, settledTo);
  const rawType = filterState[LIVE_LEDGER_TYPE_QUERY_KEY];
  const selectedType = TYPE_VALID_VALUES.includes(rawType ?? "")
    ? rawType
    : undefined;
  const searchValue = filterState[LIVE_LEDGER_SEARCH_QUERY_KEY] || undefined;

  const { categoriesForTransaction } = useLiveLedgerCategories();

  const summaryCaption = useMemo(
    () =>
      buildSummaryCaption(isSummaryFiltered, settledFrom, settledTo, (id) =>
        l10n.getString(id)
      ),
    [isSummaryFiltered, settledFrom, settledTo, l10n]
  );

  const { transactions, total, isLoading, isFetching, error, refetch } =
    useTransactionsList({
      page,
      perPage: pageSize,
      accountIds: selectedAccountIds,
      categories: selectedCategories,
      baiCodes: selectedBaiCodes,
      methods: selectedMethods,
      amountMin,
      amountMax,
      settledFrom,
      settledTo,
      enabled: !isScopedToNoAccounts && !hasFutureDates,
      type: selectedType,
      search: searchValue,
      includeReceipts: receiptsActive,
    });
  const { downloadExport, isExporting, isTooLarge } = useTransactionExport({
    accountIds: selectedAccountIds,
    categories: selectedCategories,
    baiCodes: selectedBaiCodes,
    methods: selectedMethods,
    amountMin: amountMin?.toString(),
    amountMax: amountMax?.toString(),
    settledFrom,
    settledTo,
    type: selectedType,
    search: searchValue,
  });

  const totalPages = pagination.enabled ? Math.ceil(total / pageSize) : 1;

  useEffect(() => {
    if (!isLoading && totalPages > 0 && page > totalPages) {
      setPage(totalPages);
    }
  }, [isLoading, page, totalPages, setPage]);

  const [searchParams, setSearchParams] = useSearchParams();
  const urlTransactionId =
    stateStorage === "local" ? null : searchParams.get(TRANSACTION_ID_PARAM);
  const isTransactionIdOnLoadedPage =
    urlTransactionId !== null &&
    transactions.some((transaction) => transaction.id === urlTransactionId);
  const isFallbackCandidate =
    !isLoading && urlTransactionId !== null && !isTransactionIdOnLoadedPage;

  const {
    selectedRow,
    openPanel,
    closePanel,
    onNext,
    onPrevious,
    isCrossingPage,
  } = useTransactionPanelSelection({
    rows: transactions,
    getRowId: (transaction) => transaction.id,
    storage: stateStorage,
    isLoading,
    isFetching,
    error,
    page,
    totalPages,
    onPageChange: setPage,
    keepUrlParam: isFallbackCandidate,
  });

  const shouldFetchFallback = isFallbackCandidate && !isCrossingPage;
  const {
    data: fallbackTransaction,
    isLoadingError: isFallbackTransactionError,
  } = useTransactionDetail(shouldFetchFallback ? urlTransactionId : null);

  const clearUrlTransactionId = useCallback(() => {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete(TRANSACTION_ID_PARAM);
        return next;
      },
      { replace: true }
    );
  }, [setSearchParams]);

  const notifiedFallbackErrorsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (
      !shouldFetchFallback ||
      !isFallbackTransactionError ||
      urlTransactionId === null
    ) {
      return;
    }
    clearUrlTransactionId();
    if (notifiedFallbackErrorsRef.current.has(urlTransactionId)) {
      return;
    }
    notifiedFallbackErrorsRef.current.add(urlTransactionId);
    sendNotification({
      type: "negative",
      text: l10n.getString("live-ledger-transaction-not-found-error"),
    });
  }, [
    shouldFetchFallback,
    isFallbackTransactionError,
    urlTransactionId,
    clearUrlTransactionId,
    sendNotification,
    l10n,
  ]);

  const detailsPanelRow = selectedRow ?? fallbackTransaction ?? null;
  const isFallbackRow = selectedRow === null && fallbackTransaction != null;

  const canWriteTransactions = useCanWriteTransactions();
  const transactionEnrichmentEnabled = useTransactionEnrichmentEnabled();

  const notifyReceiptError = useCallback(
    (backendCode: string | undefined) => {
      const { ftlId, vars } = pickErrorMapping(
        backendCode,
        receiptConfig?.max_size_bytes ?? 0
      );
      sendNotification({ type: "negative", text: l10n.getString(ftlId, vars) });
    },
    [receiptConfig, l10n, sendNotification]
  );

  const handleFileChosen = useCallback(
    async (transaction: API.Transaction, file: File) => {
      if (!receiptConfig) {
        return;
      }
      if (!receiptConfig.allowed_mime_types.includes(file.type)) {
        notifyReceiptError("unsupported_mime_type");
        return;
      }
      if (file.size > receiptConfig.max_size_bytes) {
        notifyReceiptError("file_too_large");
        return;
      }

      try {
        await upload(transaction.id, file);
        sendNotification({
          type: "success",
          text: l10n.getString("live-ledger-receipt-upload-success"),
        });
        setAwaitingThumbnail((pending) => ({
          ...pending,
          [transaction.id]: RECEIPT_THUMBNAIL_POLL_ATTEMPTS,
        }));
        refetch();
        queryClient.invalidateQueries({
          queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transaction.id],
        });
      } catch (uploadError) {
        const backendCode = (
          uploadError as { response?: { data?: { id?: string } } }
        )?.response?.data?.id;
        notifyReceiptError(backendCode);
      }
    },
    [
      receiptConfig,
      notifyReceiptError,
      upload,
      sendNotification,
      l10n,
      refetch,
      queryClient,
    ]
  );

  useEffect(() => {
    const pendingIds = Object.keys(awaitingThumbnail);
    if (pendingIds.length === 0) {
      return undefined;
    }

    const thumbnailFor = (id: string) =>
      id === fallbackTransaction?.id
        ? fallbackTransaction?.receipt?.thumbnail_url
        : transactions.find((transaction) => transaction.id === id)?.receipt
            ?.thumbnail_url;
    const arrived = pendingIds.filter((id) => Boolean(thumbnailFor(id)));
    if (arrived.length > 0) {
      setAwaitingThumbnail((pending) => {
        const next = { ...pending };
        arrived.forEach((id) => delete next[id]);
        return next;
      });
      return undefined;
    }

    const timer = setTimeout(() => {
      setAwaitingThumbnail((pending) => {
        const next: Record<string, number> = {};
        Object.entries(pending).forEach(([id, attemptsLeft]) => {
          if (attemptsLeft > 1) {
            next[id] = attemptsLeft - 1;
          }
        });
        return next;
      });
      refetch();
      pendingIds.forEach((id) => {
        queryClient.invalidateQueries({
          queryKey: [TRANSACTION_DETAIL_QUERY_KEY, id],
        });
      });
    }, RECEIPT_THUMBNAIL_POLL_MS);
    return () => clearTimeout(timer);
  }, [
    awaitingThumbnail,
    transactions,
    fallbackTransaction,
    refetch,
    queryClient,
  ]);

  const handleRemove = useCallback(
    async (
      transaction: API.Transaction,
      { deleteReceipt }: { deleteReceipt: boolean }
    ) => {
      try {
        await remove(transaction.id, deleteReceipt);
        sendNotification({
          type: "success",
          text: l10n.getString("live-ledger-panel-receipt-remove-success"),
        });
        refetch();
        queryClient.invalidateQueries({
          queryKey: [TRANSACTION_DETAIL_QUERY_KEY, transaction.id],
        });
      } catch {
        sendNotification({
          type: "negative",
          text: l10n.getString("live-ledger-panel-receipt-remove-error"),
        });
      }
    },
    [remove, sendNotification, l10n, refetch, queryClient]
  );

  const handleDescriptionUpdate = useCallback(() => {
    refetch();
    if (detailsPanelRow) {
      queryClient.invalidateQueries({
        queryKey: [TRANSACTION_DETAIL_QUERY_KEY, detailsPanelRow.id],
      });
    }
  }, [refetch, queryClient, detailsPanelRow]);

  const singleAccount = selectedAccountIds.length === 1;
  const showBalance = shouldShowBalanceColumn(selectedAccountIds, filterState);

  const columns = useMemo(() => {
    const availableColumns = buildLedgerColumns({
      l10n,
      accounts: accounts ?? [],
      categoriesForTransaction,
      canWriteTransactions,
      isMultiEntity,
      organizationFallbackName: currentUser?.org_name,
      receipts: receiptsActive
        ? {
            canWrite: canWriteReceipts,
            uploadingTransactionIds,
            acceptMimeTypes: receiptConfig?.allowed_mime_types,
            onFileChosen: handleFileChosen,
            onPreview: (transaction) =>
              setPreviewReceipt(transaction.receipt ?? null),
          }
        : undefined,
    });

    return columnIds
      .map((id) => availableColumns.find((column) => column.id === id))
      .filter(
        (column): column is TransactionTableColumn<API.Transaction> =>
          column !== undefined
      )
      .map((column) => {
        if (column.id === "account" && singleAccount)
          return { ...column, minBreakpoint: "none" as const };
        if (column.id === "balance" && !showBalance)
          return { ...column, minBreakpoint: "none" as const };
        return column;
      });
  }, [
    columnIds,
    singleAccount,
    showBalance,
    l10n,
    accounts,
    categoriesForTransaction,
    canWriteTransactions,
    isMultiEntity,
    currentUser?.org_name,
    receiptsActive,
    canWriteReceipts,
    uploadingTransactionIds,
    receiptConfig,
    handleFileChosen,
  ]);

  const panelSections = useMemo<
    TransactionPanelSection<API.Transaction>[]
  >(() => {
    const availableSections = buildLedgerPanelSections({
      l10n,
      accounts: accounts ?? [],
      categoriesForTransaction,
      canWrite: canWriteTransactions,
      transactionEnrichmentEnabled,
      isMultiEntity,
      organizationFallbackName: currentUser?.org_name,
      receipts: receiptsActive
        ? {
            canWrite: canWriteReceipts,
            uploadingTransactionIds,
            acceptMimeTypes: receiptConfig?.allowed_mime_types,
            onFileChosen: handleFileChosen,
            onRemove: handleRemove,
          }
        : undefined,
      isPanelOpen: detailsPanelRow !== null,
      onDescriptionUpdate: handleDescriptionUpdate,
    });
    if (!panel) {
      return availableSections;
    }
    return panel
      .map((id) => availableSections.find((section) => section.id === id))
      .filter(
        (section): section is TransactionPanelSection<API.Transaction> =>
          section !== undefined
      );
  }, [
    panel,
    l10n,
    accounts,
    categoriesForTransaction,
    canWriteTransactions,
    transactionEnrichmentEnabled,
    isMultiEntity,
    currentUser?.org_name,
    receiptsActive,
    canWriteReceipts,
    uploadingTransactionIds,
    receiptConfig,
    detailsPanelRow,
    handleFileChosen,
    handleRemove,
    handleDescriptionUpdate,
  ]);

  const notifiedErrorsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!error) {
      return;
    }
    const errorKey = `${page}:${error}`;
    if (notifiedErrorsRef.current.has(errorKey)) {
      return;
    }
    notifiedErrorsRef.current.add(errorKey);
    sendNotification({
      type: "negative",
      text: l10n.getString("live-ledger-list-error"),
    });
  }, [error, page, l10n, sendNotification]);

  return (
    <div data-testid="live-ledger-ledger-tab">
      <TransactionLedgerFilters
        filterIds={filterIds}
        filtersAlwaysVisible={filtersAlwaysVisible}
        search={search}
        scopedAccountIds={scopedAccountIds}
        resetPage={resetPage}
      />
      {showSummary && (
        <div className="margin--bottom--xl">
          <TransactionSummary
            summary={summary}
            isFiltered={isSummaryFiltered}
            caption={summaryCaption}
            isLoading={isSummaryLoading}
            error={summaryError}
            onExport={() => setIsExportDialogOpen(true)}
            exportDisabled={hasFutureDates}
            hasFutureDates={hasFutureDates}
          />
        </div>
      )}
      <TransactionTable
        columns={columns}
        rows={transactions}
        getRowId={(transaction) => transaction.id}
        renderMobileRow={(transaction) => (
          <LedgerMobileRow
            transaction={transaction}
            accounts={accounts ?? []}
            l10n={l10n}
            showBalance={showBalance}
          />
        )}
        onRowClick={openPanel}
        isLoading={isLoading}
        emptyState={emptyState}
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        headerFill={headerFill}
        pageSize={pageSize}
        pageSizeOptions={
          pagination.enabled ? pagination.pageSizeOptions : undefined
        }
        onPageSizeChange={handlePageSizeChange}
      />
      <TransactionDetailsPanel
        row={detailsPanelRow}
        header={(transaction) => (
          <LedgerPanelHeader transaction={transaction} />
        )}
        sections={panelSections}
        onClose={closePanel}
        onNext={isFallbackRow ? undefined : onNext}
        onPrev={isFallbackRow ? undefined : onPrevious}
        isLoading={isCrossingPage}
        footer={
          detailsPanelRow && !isCrossingPage ? (
            <LedgerPanelFooter
              transaction={detailsPanelRow}
              onReportIssue={setReportedTransaction}
            />
          ) : undefined
        }
      />
      {receiptsActive && (
        <ReceiptPreviewOverlay
          receipt={previewReceipt}
          onClose={() => setPreviewReceipt(null)}
        />
      )}
      {reportedTransaction && (
        <NewThreadDialog
          isDialogOpen
          closeDialog={() => setReportedTransaction(null)}
          transactionId={reportedTransaction.id}
          accountNumber={accountNumberFor(reportedTransaction, accounts ?? [])}
          replyTime={replyTime}
        />
      )}
      <ExportDialog
        isOpen={isExportDialogOpen}
        onClose={() => setIsExportDialogOpen(false)}
        onDownload={async (params) => {
          const success = await downloadExport(params);
          if (success) setIsExportDialogOpen(false);
        }}
        isTooLarge={isTooLarge}
        isExporting={isExporting}
      />
    </div>
  );
};

const TransactionLedger = (props: TransactionLedgerProps) => {
  const filterKeys = useMemo(
    () => queryKeysForFilters(props.filters ?? [], Boolean(props.search)),
    [props.filters, props.search]
  );
  return (
    <FilterProvider filterKeys={filterKeys} storage={props.stateStorage}>
      <TransactionLedgerContent {...props} />
    </FilterProvider>
  );
};

export default TransactionLedger;
